import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { useSelector, useDispatch } from "react-redux"

import { EDIT, PENDING, IDLE } from "const"
import { MtHelp } from "server/models"
import * as routes from "client/parts/routes"
import { StateType, hasAccess } from "client/utils"
import {
    PRM_HELP,
    StoreType,
    LoadHelp,
    SaveHelp,
    setHelpList,
} from "client/store"
import {
    QuestionIcon,
    Loader,
    FormGroup,
    Label,
    Input,
    Feedback,
} from "client/components"


const SidebarHelpWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${(props) => props.theme.sidebarHelpBackground};
    
    & > i {
        font-size: 20px;
        color: ${(props) => props.theme.sidebarHelpColor};
    }
`
const EditBlockWrapper = styled.div`
    position: absolute;
    z-index: 10;
    top: 46px;
    right: 0px;
    height: fit-content;
    width: 210px;
    padding: 10px;
    border-radius: 2px;
    border: 1px solid ${(props) => props.theme.sidebarHelpEditBlockBorderColor};
    background: ${(props) => props.theme.sidebarHelpEditBlockBackground};
`
const HelpLinkWrapper = styled.div`
    margin-bottom: 10px;
`
const HelpLink = styled.a`
    color: ${(props) => props.theme.sidebarHelpLinkColor};
    
    &:hover {
        color: ${(props) => props.theme.sidebarHelpLinkHoverColor};
    }
`


let isMounted: boolean
const defaultUrl = "https://risknowlogy.com/company/manual/"


const availableRoutes: string[] = []
Object.values(routes).forEach((route) => {
    if (typeof route === "string") availableRoutes.push(route)
})
// order routes by length desc
availableRoutes.sort((a, b) => (a.length < b.length ? 1 : -1))


const SidebarHelp = () => {
    const helpList = useSelector((state: StoreType) => state.help.helpList)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const popupRef = useRef<HTMLDivElement>(null)
    const [currentPage, setCurrentPage] = useState<string>(null) // Help table identifier
    const [link, setLink] = useState<string>(null)
    const [article, setArticle] = useState<string>("") // Help table value
    const [showPopup, setShowPopup] = useState(false)
    const [loadState, setLoadState] = useState<StateType>(IDLE)

    const editAccess = hasAccess(PRM_HELP, EDIT)

    useEffect(() => {
        LoadHelp().then((res) => dispatch(setHelpList(res)))

        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        const togglePopup = (e: MouseEvent) => {
            if (popupRef.current && popupRef.current.contains(e.target as Node)) return
            if (showPopup) setShowPopup(false)
        }

        document.addEventListener("click", togglePopup)
        return () => {
            document.removeEventListener("click", togglePopup)
        }
    }, [showPopup])

    useEffect(() => {
        if (!showPopup) return
        const articleId = helpList && currentPage ? helpList[currentPage] : null
        const url = createHelpLink(articleId)

        setArticle(articleId ? articleId.toString() : "")
        setLink(url)
    }, [showPopup, helpList, currentPage])

    const createHelpLink = (articleId: number) => (
        articleId ? `https://risknowlogy.com/support/detail/${articleId}/` : null
    )

    const openHelp = () => {
        const pathname = `${window.location.pathname}/` // close the route with slash
        const matchObject: { [matchIndex: number]: string } = {}
        availableRoutes.forEach((route) => {
            const matchIndex = pathname.indexOf(`${route}/`)
            if (matchIndex >= 0 && !matchObject[matchIndex]) matchObject[matchIndex] = route
        })

        const matchArray = Object.values(matchObject)
        const page = matchArray[matchArray.length - 1] // the last elem
        const articleId = helpList[page] || null
        const url = createHelpLink(articleId)

        if (editAccess) {
            setCurrentPage(page)
            setLink(url)
            setShowPopup(true)
        } else window.open(url || defaultUrl, "_blank")
    }

    const onSave = (e: React.KeyboardEvent) => {
        if (e.key !== "Enter") return
        if (article.trim().length === 0) return

        setLoadState(PENDING)

        const help: MtHelp = {
            route: currentPage,
            articleId: parseInt(article.trim(), 10),
        }

        SaveHelp(help).then(() => {
            LoadHelp().then((res) => {
                dispatch(setHelpList(res))
                if (isMounted) setLoadState(IDLE)
            })
        })
    }

    return (
        <SidebarHelpWrapper onClick={openHelp}>
            <QuestionIcon />

            {
                showPopup && (
                    <EditBlockWrapper ref={popupRef}>
                        {
                            loadState === PENDING && (
                                <Loader
                                    paddingTop={30}
                                    points={10}
                                    pointSize={5}
                                    size={40}
                                />
                            )
                        }
                        {
                            link && (
                                <HelpLinkWrapper>
                                    <HelpLink href={link} target="_blank" rel="noreferrer">{lang.viewHelp}</HelpLink>
                                </HelpLinkWrapper>
                            )
                        }

                        <FormGroup>
                            <Label>{lang.articleId}</Label>
                            <Input
                                type="text"
                                value={article}
                                onChange={(e) => {
                                    const articleId = parseInt(e.target.value, 10)
                                    setArticle(articleId ? articleId.toString() : "")
                                }}
                                onKeyPress={onSave}
                            />
                            <Feedback variant="success">{lang.clickEnterToConfirm}</Feedback>
                        </FormGroup>
                    </EditBlockWrapper>
                )
            }
        </SidebarHelpWrapper>
    )
}
export default SidebarHelp
